import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Lines (`productLine`, CONTEXT.md) in the admin: the products that are one refill in different
 * packs (Floorwipes XXL 20, 60 and 100). A product is in one line at most; which one is set in the
 * product editor (`product.setLine`). Every write answers the whole list, by name.
 */

/** A product in a line, as the list names it. */
export const ProductLineProduct = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
});
export type ProductLineProduct = z.infer<typeof ProductLineProduct>;

export const ProductLine = z.object({
	id: z.string(),
	name: z.string(),
	/** The products in the line, archived ones left out, by name. */
	products: z.array(ProductLineProduct),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type ProductLine = z.infer<typeof ProductLine>;

/** A line's name: "Floorwipes XXL". Unique. */
export const ProductLineFields = z.object({
	name: z.string().trim().min(1).max(100),
});
export type ProductLineFields = z.infer<typeof ProductLineFields>;

const ById = z.object({ productLineId: z.string().min(1) });

export const ProductLineContract = {
	list: oc.route({ method: "GET", path: "/admin/product-lines" }).output(z.array(ProductLine)),
	/** Errors: `ProductLineNameTaken` (409). */
	create: oc
		.route({ method: "POST", path: "/admin/product-lines" })
		.input(ProductLineFields)
		.output(z.array(ProductLine)),
	/** Errors: `ProductLineNotFound` (404), `ProductLineNameTaken` (409). */
	update: oc
		.route({ method: "PUT", path: "/admin/product-lines/{productLineId}" })
		.input(ProductLineFields.extend(ById.shape))
		.output(z.array(ProductLine)),
	/**
	 * Deletes a line without products. Refused while a product the list shows is in it: take it out
	 * in the product editor first. Archived products leave the line with it. Errors:
	 * `ProductLineNotFound` (404), `ProductLineNotEmpty` (409).
	 */
	delete: oc
		.route({ method: "DELETE", path: "/admin/product-lines/{productLineId}" })
		.input(ById)
		.output(z.array(ProductLine)),
};
